import { Inject, Injectable, forwardRef } from "@angular/core";
import * as moment from 'moment-timezone';
import * as moment_tz from 'moment-timezone';
import { LOCALE_TIMEZONE } from "../common/config";
import { ContextService } from "../service/context.service";

@Injectable()
export class DataAggregationUtility {

    private timezone: string;

    constructor(
        @Inject(forwardRef(() => ContextService)) private contextService: ContextService
    ) { }

    aggregateData(data: any[], options: DataAggregationOptions): any[] {
        if (!data?.length) {
            return [];
        }
        this.timezone = this.contextService.getTimezone();
        options.timestampFieldName = options.timestampFieldName || 'timestamp';
        options.endTimestampFieldName = options.endTimestampFieldName || 'endTimestamp';
        options.valueFieldName = options.valueFieldName || 'value';
        options.timeframe = options.timeframe || Timeframe.NONE;
        options.startTimestamp = options.startTimestamp != null ? options.startTimestamp : this.getTimestamp(data[data.length - 1][options.timestampFieldName]);
        options.endTimestamp = options.endTimestamp != null ? options.endTimestamp : this.getTimestamp(data[0][options.timestampFieldName]);
        if (options.timeframe == Timeframe.AUTO) {
            options.timeframe = this.getSuggestedTimeframe(options);
        }
        if (!this.isOptionsValid(options)) {
            return [];
        }
        const fullInterval = { startTimestamp: options.startTimestamp, endTimestamp: options.endTimestamp };
        const intervals: Interval[] = this.split(options.timeframe, fullInterval).reverse();    // intervals descending
        return this.performAggregation(data, intervals, options);
    }

    private getSuggestedTimeframe(options: DataAggregationOptions): Timeframe {
        const duration = moment.duration(options.endTimestamp - options.startTimestamp);
        if (duration.asDays() <= 2) {
            return Timeframe.MINUTES_5;
        } else if (duration.asDays() <= 5) {
            return Timeframe.MINUTES_10;
        } else if (duration.asDays() <= 10) {
            return Timeframe.MINUTES_30;
        } else if (duration.asDays() <= 20) {
            return Timeframe.HOURS_1;
        } else if (duration.asDays() <= 120) {
            return Timeframe.HOURS_6;
        } else if (duration.asMonths() <= 8) {
            return Timeframe.HOURS_12;
        } else if (duration.asMonths() <= 20) {
            return Timeframe.DAYS_1;
        } else {
            return Timeframe.MONTHS_1;
        }
    }

    private isOptionsValid(options: DataAggregationOptions): boolean {
        let isValid = true;
        if (!options.aggregateFunction) {
            console.error("Missing aggregateFunction");
            isValid = false;
        }
        if (options.endTimestamp < options.startTimestamp) {
            console.error("startTimestamp can't be before endTimestamp");
            isValid = false;
        }
        return isValid;
    }

    private split(timeframe: Timeframe, fullInterval: Interval): Interval[] {
        switch (timeframe) {
            case Timeframe.NONE:
                return [fullInterval];
            case Timeframe.MINUTES_1:
                return this.splitIntervals(1, fullInterval, 'minutes', 'minute');
            case Timeframe.MINUTES_5:
                return this.splitIntervals(5, fullInterval, 'minutes', 'minute');
            case Timeframe.MINUTES_10:
                return this.splitIntervals(10, fullInterval, 'minutes', 'minute');
            case Timeframe.MINUTES_15:
                return this.splitIntervals(15, fullInterval, 'minutes', 'minute');
            case Timeframe.MINUTES_30:
                return this.splitIntervals(30, fullInterval, 'minutes', 'minute');
            case Timeframe.HOURS_1:
                return this.splitIntervals(1, fullInterval, 'hours', 'hour');
            case Timeframe.HOURS_6:
                return this.splitIntervals(6, fullInterval, 'hours', 'hour');
            case Timeframe.HOURS_12:
                return this.splitIntervals(12, fullInterval, 'hours', 'hour');
            case Timeframe.DAYS_1:
                return this.splitIntervals(1, fullInterval, 'days', 'day');
            case Timeframe.DAYS_7:
                return this.splitIntervals(7, fullInterval, 'days', 'day');
            case Timeframe.DAYS_14:
                return this.splitIntervals(14, fullInterval, 'days', 'day');
            case Timeframe.WEEKS_1:
                return this.splitIntervals(1, fullInterval, 'weeks', 'isoWeek');
            case Timeframe.MONTHS_1:
                return this.splitIntervals(1, fullInterval, 'months', 'month');
            case Timeframe.MONTHS_2:
                return this.splitIntervals(2, fullInterval, 'months', 'month');
            case Timeframe.MONTHS_6:
                return this.splitIntervals(6, fullInterval, 'months', 'month');
            case Timeframe.YEARS_1:
                return this.splitIntervals(1, fullInterval, 'years', 'year');
            default:
                console.error("Invalid timeframe");
                return [];
        }
    }

    splitIntervals(quantity: number, fullInterval: Interval, duration: moment_tz.DurationInputArg2, unitOfTime: moment_tz.unitOfTime.StartOf): Interval[] {
        let intervals: Interval[] = [];
        let currentStart = moment_tz.tz(fullInterval.startTimestamp, this.timezone || LOCALE_TIMEZONE);
        currentStart.startOf(unitOfTime);
        const end = moment_tz.tz(fullInterval.endTimestamp, this.timezone || LOCALE_TIMEZONE);
        while (currentStart.isBefore(end)) {
            const startTimestamp = currentStart.valueOf();
            const endTimestamp = currentStart.add(quantity, duration).startOf(unitOfTime).valueOf();
            intervals.push({ startTimestamp: startTimestamp, endTimestamp: Math.min(endTimestamp - 1, fullInterval.endTimestamp) });
        }
        return intervals;
    }

    private performAggregation(data: any[], intervals: Interval[], options: DataAggregationOptions): any[] {
        let results = [];
        let intervalIndex: number = 0;
        if (intervals?.length && data?.length) {
            data = data.filter(d => this.getTimestamp(d[options.timestampFieldName]) >= options.startTimestamp && this.getTimestamp(d[options.timestampFieldName]) <= options.endTimestamp);
            let currentIntervalAggregationDetails: { min: number, max: number, sum: number, latestValue: number, elementCount: number };
            for (let i = 0; i < data.length; i++) {
                const d = data[i];
                const dataValue = Number(d[options.valueFieldName]);
                const dataTimestamp = this.getTimestamp(d[options.timestampFieldName]);
                if (isNaN(dataValue)) {
                    console.error("Invalid data. Only numeric values are allowed");
                    results = [];
                    break;
                }
                if (dataTimestamp >= intervals[intervalIndex].startTimestamp && dataTimestamp <= intervals[intervalIndex].endTimestamp) {
                    if (currentIntervalAggregationDetails) {
                        currentIntervalAggregationDetails.sum += dataValue;
                        currentIntervalAggregationDetails.min = Math.min(currentIntervalAggregationDetails.min, dataValue);
                        currentIntervalAggregationDetails.max = Math.max(currentIntervalAggregationDetails.max, dataValue);
                        currentIntervalAggregationDetails.elementCount++;
                    } else {
                        currentIntervalAggregationDetails = { min: dataValue, max: dataValue, sum: dataValue, latestValue: dataValue, elementCount: 1 };
                    }
                } else {
                    const aggregationValue = this.getAggregationValue(options.aggregateFunction, currentIntervalAggregationDetails, dataValue);
                    if (aggregationValue != null) {
                        let result = {};
                        result[options.timestampFieldName] = intervals[intervalIndex].startTimestamp;
                        result[options.valueFieldName] = aggregationValue;
                        result[options.endTimestampFieldName] = intervals[intervalIndex].endTimestamp;
                        results.push(result);
                    }
                    intervalIndex++;
                    while (intervalIndex < intervals.length) {
                        if (dataTimestamp >= intervals[intervalIndex].startTimestamp && dataTimestamp <= intervals[intervalIndex].endTimestamp) {
                            currentIntervalAggregationDetails = { min: dataValue, max: dataValue, sum: dataValue, latestValue: dataValue, elementCount: 1 };
                            break;
                        }
                        intervalIndex++;
                    }
                    if (intervalIndex == intervals.length) {
                        console.error("Invalid data. Values must be in descending order");
                        results = [];
                        break;
                    }
                }
                if (i == (data.length - 1)) {
                    const aggregationValue = this.getAggregationValue(options.aggregateFunction, currentIntervalAggregationDetails, null);
                    if (aggregationValue != null) {
                        let result = {};
                        result[options.timestampFieldName] = intervals[intervalIndex].startTimestamp;
                        result[options.valueFieldName] = aggregationValue;
                        result[options.endTimestampFieldName] = intervals[intervalIndex].endTimestamp;
                        results.push(result);
                    }
                }
            };
        }
        return results;
    }

    private getAggregationValue(aggregation: Aggregation, aggregationDetails: { min: number, max: number, sum: number, latestValue: number, elementCount: number }, currentValue: number): number {
        if (aggregationDetails) {
            switch (aggregation) {
                case Aggregation.DELTA:
                    return currentValue != null && aggregationDetails.latestValue != null ? (aggregationDetails.latestValue - currentValue) : null;
                case Aggregation.AVG:
                    return aggregationDetails.elementCount > 0 ? (aggregationDetails.sum / aggregationDetails.elementCount) : null;
                case Aggregation.MIN:
                    return aggregationDetails.min;
                case Aggregation.MAX:
                    return aggregationDetails.max;
                case Aggregation.SUM:
                    return aggregationDetails.sum;
                default:
                    return null;
            }
        } else {
            return null;
        }
    }

    private getTimestamp(value: any): number {
        return value * 1;   // * 1 enforces Number (value may be Date)
    }

}

export class Interval {
    startTimestamp: number;
    endTimestamp: number;
}

export enum Timeframe {
    NONE = "NONE",
    MINUTES_1 = "MINUTES_1",
    MINUTES_5 = "MINUTES_5",
    MINUTES_10 = "MINUTES_10",
    MINUTES_15 = "MINUTES_15",
    MINUTES_30 = "MINUTES_30",
    HOURS_1 = "HOURS_1",
    HOURS_6 = "HOURS_6",
    HOURS_12 = "HOURS_12",
    DAYS_1 = "DAYS_1",
    DAYS_7 = "DAYS_7",
    DAYS_14 = "DAYS_14",
    WEEKS_1 = "WEEKS_1",
    MONTHS_1 = "MONTHS_1",
    MONTHS_2 = "MONTHS_2",
    MONTHS_6 = "MONTHS_6",
    YEARS_1 = "YEARS_1",
    AUTO = "AUTO"
}

export enum Aggregation {
    DELTA = 'DELTA',
    AVG = 'AVG',
    MIN = 'MIN',
    MAX = 'MAX',
    SUM = 'SUM'
}

export class DataAggregationOptions {
    timestampFieldName: string;
    endTimestampFieldName: string;
    valueFieldName: string;
    aggregateFunction: Aggregation;
    timeframe: Timeframe;
    startTimestamp: number;
    endTimestamp: number;
}